import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, ChevronLeft, Save, Loader2, AlertCircle, Plus, Minus, Smartphone, Tablet, Wifi, Router } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useT } from "../lib/i18n";
import { toast } from "sonner";

const MAX_LINES = 10;

// Cross-category bundle discounts. Applied to EACH line of these categories
// when the bill has at least one Phones line. Discount is per-line per-month;
// line_price never goes negative.
const BUNDLE_DISCOUNTS = [
  { categoryMatch: "tablet",        amount: 40, label: "Phone bundle discount −$40" },
  { categoryMatch: "home internet", amount: 25, label: "Phone bundle discount −$25" },
];

function discountForCategory(categoryName) {
  const n = (categoryName || "").toLowerCase();
  return BUNDLE_DISCOUNTS.find((d) => n.includes(d.categoryMatch));
}

// Map category name → icon. Unknown names fall back to Smartphone.
function iconForCategory(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("phone")) return Smartphone;
  if (n.includes("tablet")) return Tablet;
  if (n.includes("home")) return Wifi;
  if (n.includes("hotspot")) return Router;
  return Smartphone;
}

function isPhonesCategory(cat) {
  return (cat?.name || "").toLowerCase() === "phones";
}

/** Tiered per-line subtotal (only used for Phones category). */
function tieredPhonesSubtotal(plan, lines) {
  if (!plan || lines <= 0) return 0;
  const tiers = plan.line_tiers || [];
  if (tiers.length > 0) {
    let total = 0;
    for (let i = 1; i <= lines; i++) {
      const tier = tiers.find((t) => i >= t.from_line && i <= t.to_line);
      total += tier ? tier.price : (plan.price_one_line || 0);
    }
    return total;
  }
  const base = plan.price_one_line || 0;
  if (lines <= 1) return base;
  const discount = (plan.multi_line_discount_pct || 0) / 100;
  return base + base * (1 - discount) * (lines - 1);
}

export default function Estimator() {
  const navigate = useNavigate();
  const { t } = useT();
  const [step, setStep] = useState(1);
  const [plans, setPlans] = useState([]);
  const [categories, setCategories] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [loading, setLoading] = useState(true);

  // mix: { [categoryId]: numberOfLines }
  const [mix, setMix] = useState({});
  // phones share one plan
  const [phonePlanId, setPhonePlanId] = useState(null);
  // other categories: each line picks its own plan. linePlans[catId] = [planId, planId, ...]
  const [linePlans, setLinePlans] = useState({});

  // Add-on selections
  const [accountAddOns, setAccountAddOns] = useState(new Set());          // Set of addon_id (applies_to_categories=[])
  const [lineAddOns, setLineAddOns] = useState({});                       // { [catId]: { [lineIdx]: Set<addonId> } }

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, c, a] = await Promise.all([
          api.get("/plans", { params: { active_only: true } }),
          api.get("/categories"),
          api.get("/addons", { params: { active_only: true } }),
        ]);
        setPlans(p.data || []);
        setCategories(c.data || []);
        setAddOns(a.data || []);
      } finally { setLoading(false); }
    })();
  }, []);

  // Plans grouped by category id
  const plansByCategory = useMemo(() => {
    const map = {};
    for (const p of plans) {
      const cid = p.category_id || "_uncategorized";
      if (!map[cid]) map[cid] = [];
      map[cid].push(p);
    }
    return map;
  }, [plans]);

  // Categories that have at least one active plan + a category id (skip uncategorized).
  const usableCategories = useMemo(
    () => (categories || []).filter((c) => (plansByCategory[c.id] || []).length > 0),
    [categories, plansByCategory]
  );

  const phonesCat = useMemo(() => usableCategories.find(isPhonesCategory), [usableCategories]);
  const totalLines = useMemo(() => Object.values(mix).reduce((s, n) => s + (n || 0), 0), [mix]);
  const atCap = totalLines >= MAX_LINES;

  const setCategoryLines = (catId, n) => {
    const next = Math.max(0, Math.min(MAX_LINES, n));
    // Enforce global cap: if increasing, ensure total doesn't exceed MAX_LINES
    const otherTotal = Object.entries(mix).reduce((s, [k, v]) => s + (k === catId ? 0 : (v || 0)), 0);
    const clamped = Math.min(next, MAX_LINES - otherTotal);
    setMix((m) => ({ ...m, [catId]: clamped }));

    // Trim linePlans for the affected non-phones category to match the new count
    if (catId !== phonesCat?.id) {
      setLinePlans((lp) => {
        const cur = lp[catId] || [];
        const trimmed = cur.slice(0, clamped);
        // Pad with nulls if grew
        while (trimmed.length < clamped) trimmed.push(null);
        return { ...lp, [catId]: trimmed };
      });
    }
  };

  // Pricing
  const itemRows = useMemo(() => {
    const phoneCount = phonesCat ? (mix[phonesCat.id] || 0) : 0;
    const rows = [];
    for (const cat of usableCategories) {
      const n = mix[cat.id] || 0;
      if (n <= 0) continue;
      // Cross-category bundle discount only applies when at least one Phones line is present.
      const bundle = !isPhonesCategory(cat) && phoneCount > 0 ? discountForCategory(cat.name) : null;
      if (isPhonesCategory(cat)) {
        const plan = plans.find((p) => p.id === phonePlanId);
        if (!plan) continue;
        const tiers = plan.line_tiers || [];
        for (let i = 1; i <= n; i++) {
          const tier = tiers.find((t) => i >= t.from_line && i <= t.to_line);
          const linePrice = tier ? tier.price : (plan.price_one_line || 0);
          rows.push({
            category_id: cat.id,
            category_name: cat.name,
            plan_id: plan.id,
            plan_name: plan.name,
            line_number: i,
            line_price: linePrice,
            original_price: null,
            discount_label: null,
          });
        }
      } else {
        const arr = linePlans[cat.id] || [];
        for (let i = 0; i < n; i++) {
          const planId = arr[i];
          const plan = plans.find((p) => p.id === planId);
          if (!plan) continue;
          const base = plan.price_one_line || 0;
          const discounted = bundle ? Math.max(0, base - bundle.amount) : base;
          rows.push({
            category_id: cat.id,
            category_name: cat.name,
            plan_id: plan.id,
            plan_name: plan.name,
            line_number: i + 1,
            line_price: discounted,
            original_price: bundle ? base : null,
            discount_label: bundle ? bundle.label : null,
          });
        }
      }
    }
    return rows;
  }, [usableCategories, mix, plans, phonePlanId, linePlans, phonesCat]);

  const total = useMemo(() => itemRows.reduce((s, r) => s + r.line_price, 0), [itemRows]);

  // Per-category groupings & validation
  const categoryGroups = useMemo(() => {
    return usableCategories
      .map((cat) => ({ cat, lines: mix[cat.id] || 0 }))
      .filter((g) => g.lines > 0);
  }, [usableCategories, mix]);

  const phonesGroup = categoryGroups.find((g) => isPhonesCategory(g.cat));
  const otherGroups = categoryGroups.filter((g) => !isPhonesCategory(g.cat));

  // Add-on partitioning — Auto Pay is ALWAYS account-level, regardless of admin settings.
  const isAutoPayAddOn = (a) => /auto\s*pay/i.test(a.name || "");
  const accountWideAddOns = useMemo(
    () => addOns.filter((a) => (a.applies_to_categories || []).length === 0 || isAutoPayAddOn(a)),
    [addOns]
  );
  const lineLevelAddOnsFor = (catId) => addOns.filter((a) => !isAutoPayAddOn(a) && (a.applies_to_categories || []).includes(catId));
  const anyAddOnsAvailable = addOns.length > 0;

  // Resolved add-on lines: each entry already represents the total monthly contribution.
  // Auto Pay safeguard: discount magnitude is capped at $5 per quote regardless of admin value.
  const addOnLines = useMemo(() => {
    const isAutoPay = (a) => /auto\s*pay/i.test(a.name || "");
    const cappedPrice = (a) => {
      if (isAutoPay(a) && (a.monthly_price || 0) < 0) return Math.max(-5, a.monthly_price);
      return a.monthly_price;
    };
    const lines = [];
    for (const a of addOns) {
      if ((a.applies_to_categories || []).length === 0) {
        if (accountAddOns && accountAddOns.has && accountAddOns.has(a.id)) {
          lines.push({ name: a.name, price: cappedPrice(a), count: 1 });
        }
      } else {
        // Count how many lines have this addon checked
        let occurrences = 0;
        for (const catId of Object.keys(lineAddOns || {})) {
          const m = lineAddOns[catId] || {};
          for (const idx of Object.keys(m)) {
            const set = m[idx];
            if (set && typeof set.has === "function" && set.has(a.id)) occurrences += 1;
          }
        }
        if (occurrences > 0) {
          // Auto Pay should never be per-line; but if admin mis-tagged it with categories, still apply only ONCE and cap.
          if (isAutoPay(a)) {
            lines.push({ name: a.name, price: cappedPrice(a), count: 1 });
          } else {
            lines.push({ name: `${a.name} × ${occurrences}`, price: a.monthly_price * occurrences, count: occurrences });
          }
        }
      }
    }
    return lines;
  }, [addOns, accountAddOns, lineAddOns]);

  const addOnsTotal = useMemo(() => addOnLines.reduce((s, l) => s + l.price, 0), [addOnLines]);
  const finalTotal = total + addOnsTotal;

  const step2Complete = useMemo(() => {
    // Phones group needs a plan if it has lines
    if (phonesGroup && !phonePlanId) return false;
    // Each non-phones line needs a plan
    for (const g of otherGroups) {
      const arr = linePlans[g.cat.id] || [];
      for (let i = 0; i < g.lines; i++) {
        if (!arr[i]) return false;
      }
    }
    return true;
  }, [phonesGroup, phonePlanId, otherGroups, linePlans]);

  // Step list — addons step is inserted only when add-ons exist
  const stepDefs = useMemo(() => {
    const list = [
      { key: "mix", title: "01 — How many of each?" },
      { key: "plans", title: "02 — Pick a plan per device type" },
    ];
    if (anyAddOnsAvailable) list.push({ key: "addons", title: `0${list.length + 1} — Add-ons` });
    list.push({ key: "customer", title: `0${list.length + 1} — Customer info` });
    return list;
  }, [anyAddOnsAvailable]);
  const currentStep = stepDefs[step - 1];

  const canNext = () => {
    const k = currentStep?.key;
    if (k === "mix") return totalLines > 0;
    if (k === "plans") return step2Complete;
    return true;
  };

  const next = () => setStep((s) => Math.min(stepDefs.length, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const toggleAccountAddOn = (id) => {
    setAccountAddOns((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleLineAddOn = (catId, idx, addonId) => {
    setLineAddOns((prev) => {
      const catMap = { ...(prev[catId] || {}) };
      const cur = catMap[idx] instanceof Set ? new Set(catMap[idx]) : new Set();
      if (cur.has(addonId)) cur.delete(addonId); else cur.add(addonId);
      catMap[idx] = cur;
      return { ...prev, [catId]: catMap };
    });
  };

  const saveQuote = async () => {
    if (itemRows.length === 0) return;
    setSaving(true);
    try {
      // Legacy fields — use the first item as the "primary" plan for back-compat.
      const primary = itemRows[0];
      const planNames = Array.from(new Set(itemRows.map((r) => r.plan_name)));
      const summaryName = planNames.length === 1 ? planNames[0] : `Mixed bill (${planNames.length} plans)`;
      const payload = {
        plan_id: primary.plan_id,
        plan_name: summaryName,
        lines: totalLines,
        base_price: primary.line_price,
        addons: addOnLines.map((l) => ({ name: l.name, price: round2(l.price) })),
        subtotal: round2(total),
        taxes_fees: round2(addOnsTotal),
        total: round2(finalTotal),
        customer_name: name || null,
        customer_email: email || null,
        customer_phone: phone || null,
        consent_to_contact: !!consent,
        bill_items: itemRows.map((r) => ({
          category_id: r.category_id,
          category_name: r.category_name,
          plan_id: r.plan_id,
          plan_name: r.plan_name,
          line_number: r.line_number,
          line_price: round2(r.line_price),
          original_price: r.original_price != null ? round2(r.original_price) : null,
          discount_label: r.discount_label || null,
        })),
      };
      const { data } = await api.post("/quotes", payload);
      toast.success("Quote saved! Redirecting…");
      navigate(`/quote/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save quote.");
    } finally { setSaving(false); }
  };

  return (
    <main className="bg-slate-50 min-h-[80vh]" data-testid="page-estimator">
      <div className="tw-container py-12 lg:py-16">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="tw-overline">{t("estimator_overline")}</div>
            <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0B132B]">
              {t("estimator_title")}
            </h1>
            <p className="mt-3 text-slate-600 max-w-xl">
              Mix and match lines across phones, tablets, home internet, and hotspots — up to {MAX_LINES} per account.
            </p>
          </div>
          <StepIndicator step={step} count={stepDefs.length} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 surface-card !p-0">
            <AnimatePresence mode="wait">
              {currentStep?.key === "mix" && (
                <StepShell key="mix" title={currentStep.title}>
                  {loading ? <CenteredLoader /> : usableCategories.length === 0 ? (
                    <EmptyNotice title="No plans configured" body="Admins need to add plans under at least one category before customers can build a bill." />
                  ) : (
                    <div className="space-y-3" data-testid="category-mix">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-slate-600">Choose lines per device type. {MAX_LINES}-line account cap.</p>
                        <span className={`badge-pill ${atCap ? "badge-amber" : "badge-outline"}`} data-testid="line-cap-badge">
                          {totalLines} / {MAX_LINES} lines
                        </span>
                      </div>
                      {usableCategories.map((cat) => {
                        const Icon = iconForCategory(cat.name);
                        const n = mix[cat.id] || 0;
                        const canInc = totalLines < MAX_LINES;
                        return (
                          <div key={cat.id} className="flex items-center justify-between gap-4 p-4 border border-slate-200" data-testid={`mix-row-${cat.id}`}>
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="h-10 w-10 bg-[#0B132B] grid place-items-center flex-shrink-0">
                                <Icon className="h-5 w-5 text-[#00E5FF]" />
                              </div>
                              <div className="min-w-0">
                                <div className="font-bold text-[#0B132B] truncate">{cat.name}</div>
                                <div className="text-xs text-slate-500 mono">{(plansByCategory[cat.id] || []).length} plan{(plansByCategory[cat.id] || []).length > 1 ? "s" : ""} available</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0" data-testid={`mix-controls-${cat.id}`}>
                              <button
                                onClick={() => setCategoryLines(cat.id, n - 1)}
                                disabled={n === 0}
                                className="h-9 w-9 border-2 border-[#0B132B] grid place-items-center hover:bg-[#0B132B] hover:text-white transition disabled:opacity-30 disabled:cursor-not-allowed"
                                data-testid={`mix-dec-${cat.id}`}
                              >
                                <Minus className="h-4 w-4" />
                              </button>
                              <div className="mono text-3xl font-bold text-[#0B132B] w-12 text-center ticker-num" data-testid={`mix-value-${cat.id}`}>{n}</div>
                              <button
                                onClick={() => setCategoryLines(cat.id, n + 1)}
                                disabled={!canInc}
                                className="h-9 w-9 border-2 border-[#0B132B] grid place-items-center hover:bg-[#0B132B] hover:text-white transition disabled:opacity-30 disabled:cursor-not-allowed"
                                data-testid={`mix-inc-${cat.id}`}
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </StepShell>
              )}

              {currentStep?.key === "plans" && (
                <StepShell key="plans" title={currentStep.title}>
                  {categoryGroups.length === 0 ? (
                    <EmptyNotice title="Nothing selected" body="Go back to step 1 and pick at least one line." />
                  ) : (
                    <div className="space-y-8">
                      {phonesGroup && (
                        <CategorySection
                          icon={iconForCategory(phonesGroup.cat.name)}
                          title={`${phonesGroup.cat.name} · ${phonesGroup.lines} line${phonesGroup.lines > 1 ? "s" : ""}`}
                          subtitle="All phone lines share one plan. Per-line tier pricing applies."
                          testKey={`section-${phonesGroup.cat.id}`}
                        >
                          <SharedPlanPicker
                            plans={plansByCategory[phonesGroup.cat.id] || []}
                            selectedId={phonePlanId}
                            onSelect={setPhonePlanId}
                            lines={phonesGroup.lines}
                            showTiers
                          />
                        </CategorySection>
                      )}
                      {otherGroups.map((g) => {
                        const phoneCount = phonesCat ? (mix[phonesCat.id] || 0) : 0;
                        const bundleDiscount = phoneCount > 0 ? discountForCategory(g.cat.name) : null;
                        return (
                          <CategorySection
                            key={g.cat.id}
                            icon={iconForCategory(g.cat.name)}
                            title={`${g.cat.name} · ${g.lines} line${g.lines > 1 ? "s" : ""}`}
                            subtitle="Each line can be a different plan."
                            testKey={`section-${g.cat.id}`}
                          >
                            <PerLinePlanPicker
                              categoryId={g.cat.id}
                              categoryName={g.cat.name}
                              plans={plansByCategory[g.cat.id] || []}
                              lines={g.lines}
                              selectedIds={linePlans[g.cat.id] || []}
                              onChange={(arr) => setLinePlans((lp) => ({ ...lp, [g.cat.id]: arr }))}
                              bundleDiscount={bundleDiscount}
                            />
                          </CategorySection>
                        );
                      })}
                    </div>
                  )}
                </StepShell>
              )}

              {currentStep?.key === "addons" && (
                <StepShell key="addons" title={currentStep.title}>
                  <AddOnsStep
                    addOns={addOns}
                    categoryGroups={categoryGroups}
                    accountWideAddOns={accountWideAddOns}
                    accountAddOns={accountAddOns}
                    onToggleAccount={toggleAccountAddOn}
                    lineAddOns={lineAddOns}
                    onToggleLine={toggleLineAddOn}
                    lineLevelAddOnsFor={lineLevelAddOnsFor}
                  />
                </StepShell>
              )}

              {currentStep?.key === "customer" && (
                <StepShell key="customer" title={currentStep.title}>
                  <div className="space-y-4" data-testid="customer-form">
                    <div>
                      <label className="tw-overline">{t("field_name")}</label>
                      <input value={name} onChange={(e) => setName(e.target.value)} className="input-sharp mt-2" placeholder="Jane Doe" data-testid="customer-name-input" />
                    </div>
                    <div>
                      <label className="tw-overline">Phone number</label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="input-sharp mt-2"
                        placeholder="(555) 123-4567"
                        autoComplete="tel"
                        data-testid="customer-phone-input"
                      />
                    </div>
                    <div>
                      <label className="tw-overline">{t("field_email")}</label>
                      <input value={email} onChange={(e) => setEmail(e.target.value)} className="input-sharp mt-2" placeholder="jane@example.com" data-testid="customer-email-input" />
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="customer-consent-label">
                      <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        className="mt-1 h-5 w-5 accent-[#00E5FF] cursor-pointer flex-shrink-0"
                        data-testid="customer-consent"
                      />
                      <span className="text-sm text-slate-700">
                        <span className="font-bold text-[#0B132B]">Consent to contact</span>
                        <span className="block text-xs text-slate-500 mt-0.5">
                          Customer agrees to be contacted about this quote and related offers.
                        </span>
                      </span>
                    </label>
                    <p className="text-xs text-slate-500">{t("field_email_note")}</p>
                  </div>
                </StepShell>
              )}
            </AnimatePresence>

            <div className="flex items-center justify-between p-6 border-t border-slate-200 bg-slate-50">
              <button onClick={prev} disabled={step === 1} className="btn-outline-navy disabled:opacity-30 disabled:cursor-not-allowed" data-testid="wizard-back">
                <ChevronLeft className="h-4 w-4" /> {t("wizard_back")}
              </button>
              {step < stepDefs.length ? (
                <button
                  onClick={next}
                  disabled={!canNext()}
                  className="btn-navy disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="wizard-next"
                >
                  {t("wizard_next")} <ChevronRight className="h-4 w-4" />
                </button>
              ) : (
                <button onClick={saveQuote} disabled={saving || itemRows.length === 0} className="btn-brand disabled:opacity-50" data-testid="wizard-save-quote">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("wizard_save")}
                </button>
              )}
            </div>
          </div>

          {/* Live quote */}
          <aside className="lg:col-span-5">
            <div className="sticky top-24 bg-[#0B132B] text-white p-8 border-t-4 border-[#00E5FF] shadow-xl" data-testid="live-quote-panel">
              <div className="tw-overline !text-[#00E5FF]">{t("live_quote")}</div>
              <div className="mt-2 text-sm text-slate-300">
                {totalLines === 0 ? "Build your line mix to start…" : `${totalLines} line${totalLines > 1 ? "s" : ""} across ${categoryGroups.length} ${categoryGroups.length === 1 ? "category" : "categories"}`}
              </div>

              <motion.div
                key={finalTotal.toFixed(2)}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="mt-6"
              >
                <div className="mono text-6xl font-bold ticker-num text-white">
                  ${finalTotal.toFixed(2)}
                </div>
                <div className="tw-overline !text-slate-400 mt-1">{t("total_monthly_est")}</div>
              </motion.div>

              <div className="mt-8 space-y-4 text-sm">
                {categoryGroups.length === 0 ? (
                  <p className="text-slate-400 italic">No lines selected yet.</p>
                ) : (
                  categoryGroups.map((g) => {
                    const groupRows = itemRows.filter((r) => r.category_id === g.cat.id);
                    const groupTotal = groupRows.reduce((s, r) => s + r.line_price, 0);
                    return (
                      <div key={g.cat.id} data-testid={`sidebar-group-${g.cat.id}`}>
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                          <div className="tw-overline !text-slate-400">{g.cat.name}</div>
                          <div className="mono text-xs text-slate-400">{g.lines} line{g.lines > 1 ? "s" : ""}</div>
                        </div>
                        {groupRows.length === 0 ? (
                          <p className="text-xs text-slate-500 italic">Pick a plan…</p>
                        ) : (
                          <>
                            {groupRows.map((r, i) => (
                              <div key={i} data-testid={`sidebar-line-${r.category_id}-${r.line_number}`}>
                                <Row
                                  label={`Line ${r.line_number} — ${r.plan_name}`}
                                  value={`$${r.line_price.toFixed(2)}`}
                                  muted={i > 0}
                                />
                                {r.original_price != null && r.discount_label && (
                                  <div className="text-[10px] text-[#00E5FF] mono uppercase tracking-widest -mt-0.5 ml-1" data-testid={`sidebar-discount-${r.category_id}-${r.line_number}`}>
                                    was ${r.original_price.toFixed(2)} · {r.discount_label}
                                  </div>
                                )}
                              </div>
                            ))}
                            <div className="flex items-baseline justify-between pt-1">
                              <span className="text-slate-300 text-xs mono uppercase tracking-widest">{g.cat.name} subtotal</span>
                              <span className="mono ticker-num font-bold text-white">${groupTotal.toFixed(2)}</span>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })
                )}
                <div className="hairline-divider !border-slate-700 my-3" />
                {addOnLines.length > 0 && (
                  <div data-testid="sidebar-addons">
                    <div className="tw-overline !text-slate-400 mb-1">Add-ons</div>
                    {addOnLines.map((l, i) => (
                      <Row
                        key={i}
                        label={l.name}
                        value={`${l.price < 0 ? "−" : ""}$${Math.abs(l.price).toFixed(2)}`}
                        muted={i > 0}
                      />
                    ))}
                    <div className="hairline-divider !border-slate-700 my-3" />
                  </div>
                )}
                <Row label={t("total_month")} value={`$${finalTotal.toFixed(2)}`} bold big />
              </div>

              <div className="mt-6 pt-6 border-t border-slate-700 text-xs text-slate-400 leading-relaxed">
                Plan prices are tax-inclusive. Device purchases include additional tax.
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

function CategorySection({ icon: Icon, title, subtitle, testKey, children }) {
  return (
    <div className="border-l-4 border-[#0B132B] pl-5" data-testid={testKey}>
      <div className="flex items-center gap-3 mb-1">
        <Icon className="h-5 w-5 text-[#0B132B]" />
        <h3 className="font-bold text-[#0B132B]">{title}</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">{subtitle}</p>
      {children}
    </div>
  );
}

function SharedPlanPicker({ plans, selectedId, onSelect, lines, showTiers }) {
  if (plans.length === 0) return <EmptyNotice title="No plans" body="No plans in this category." />;
  return (
    <div className="space-y-3" data-testid="shared-plan-picker">
      {plans.map((p) => {
        const sel = p.id === selectedId;
        const subtotal = tieredPhonesSubtotal(p, lines);
        return (
          <button
            key={p.id}
            onClick={() => onSelect(p.id)}
            className={`w-full text-left p-4 border-2 transition-all ${sel ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-slate-200 hover:border-slate-400"}`}
            data-testid={`shared-plan-${p.id}`}
          >
            <div className="flex items-baseline justify-between gap-4">
              <div>
                <div className="font-bold text-[#0B132B]">{p.name}</div>
                <div className="text-xs mono text-slate-500 mt-1">
                  {p.unlimited ? "Unlimited" : `${p.data_gb} GB`} data
                  {p.hotspot_unlimited ? " · Unlimited hotspot" : (p.hotspot_gb > 0 ? ` · ${p.hotspot_gb} GB hotspot` : "")}
                </div>
                {showTiers && p.line_tiers?.length > 0 && (
                  <div className="mt-2 inline-flex items-center gap-1.5"><span className="badge-pill badge-teal text-[10px]">Per-line tiers</span></div>
                )}
              </div>
              <div className="text-right">
                <div className="mono text-2xl font-bold text-[#0B132B] ticker-num">
                  ${subtotal.toFixed(0)}<span className="text-xs text-slate-500 font-normal">/mo</span>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">for {lines} line{lines > 1 ? "s" : ""}</div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function PerLinePlanPicker({ categoryId, categoryName, plans, lines, selectedIds, onChange, bundleDiscount }) {
  if (plans.length === 0) return <EmptyNotice title="No plans" body="No plans in this category." />;
  const setLine = (idx, planId) => {
    const next = [...selectedIds];
    while (next.length < lines) next.push(null);
    next[idx] = planId;
    onChange(next.slice(0, lines));
  };
  return (
    <div className="space-y-3" data-testid={`per-line-picker-${categoryId}`}>
      {bundleDiscount && (
        <div className="border-l-4 border-[#00E5FF] bg-[#00E5FF]/10 p-3 text-xs text-[#0B132B]" data-testid={`per-line-discount-banner-${categoryId}`}>
          <strong className="mono uppercase tracking-widest">Phone-bundle saving applied:</strong> ${bundleDiscount.amount.toFixed(0)} off each {categoryName} line because the customer has at least one phone line.
        </div>
      )}
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="border border-slate-200 p-3" data-testid={`per-line-row-${categoryId}-${i + 1}`}>
          <div className="tw-overline mb-2">Line {i + 1}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {plans.map((p) => {
              const sel = selectedIds[i] === p.id;
              const base = p.price_one_line || 0;
              const discounted = bundleDiscount ? Math.max(0, base - bundleDiscount.amount) : base;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setLine(i, p.id)}
                  className={`text-left px-3 py-2 border-2 transition ${sel ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-slate-200 hover:border-slate-400"}`}
                  data-testid={`per-line-plan-${categoryId}-${i + 1}-${p.id}`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-semibold text-[#0B132B] text-sm truncate">{p.name}</span>
                    <span className="text-right">
                      {bundleDiscount ? (
                        <>
                          <span className="mono text-[10px] line-through text-slate-400 mr-1">${base.toFixed(0)}</span>
                          <span className="mono text-sm font-bold text-[#0B132B] ticker-num">${discounted.toFixed(0)}</span>
                        </>
                      ) : (
                        <span className="mono text-sm font-bold text-[#0B132B] ticker-num">${base.toFixed(0)}</span>
                      )}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function StepShell({ title, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
      transition={{ duration: 0.25 }}
      className="p-8"
    >
      <div className="tw-overline">{title.split(" — ")[0]}</div>
      <h2 className="mt-2 text-2xl font-bold text-[#0B132B]">{title.split(" — ")[1]}</h2>
      <div className="mt-6">{children}</div>
    </motion.div>
  );
}

function StepIndicator({ step, count }) {
  const list = Array.from({ length: count || 3 }, (_, i) => i + 1);
  return (
    <div className="flex items-center gap-2" data-testid="step-indicator">
      {list.map((n) => (
        <div
          key={n}
          className={`mono text-xs h-8 w-8 grid place-items-center border-2 ${
            n === step ? "border-[#00E5FF] bg-[#00E5FF] text-[#0B132B]" : n < step ? "border-[#0B132B] bg-[#0B132B] text-white" : "border-slate-300 text-slate-400"
          }`}
        >
          {n}
        </div>
      ))}
    </div>
  );
}

function Row({ label, value, bold, big, muted }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className={`${muted ? "text-slate-400" : "text-slate-200"} ${bold ? "font-semibold" : ""} text-xs sm:text-sm`}>{label}</span>
      <span className={`mono ticker-num ${bold ? "font-bold" : "font-medium"} ${big ? "text-2xl text-[#00E5FF]" : ""}`}>{value}</span>
    </div>
  );
}

function EmptyNotice({ title, body }) {
  return (
    <div className="py-10 text-center border-2 border-dashed border-slate-300" data-testid="estimator-empty">
      <AlertCircle className="h-7 w-7 mx-auto text-amber-500" />
      <p className="mt-3 text-slate-700 font-semibold">{title}</p>
      <p className="text-sm text-slate-500 mt-1">{body}</p>
    </div>
  );
}

function CenteredLoader() {
  return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;
}

function AddOnsStep({ addOns, categoryGroups, accountWideAddOns, accountAddOns, onToggleAccount, lineAddOns, onToggleLine, lineLevelAddOnsFor }) {
  return (
    <div className="space-y-8" data-testid="addons-step">
      {accountWideAddOns.length > 0 && (
        <div data-testid="account-addons">
          <div className="tw-overline mb-3">Account-wide</div>
          <div className="space-y-2">
            {accountWideAddOns.map((a) => {
              const isDiscount = (a.monthly_price || 0) < 0;
              const sel = accountAddOns.has(a.id);
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => onToggleAccount(a.id)}
                  className={`w-full text-left p-4 border-2 transition flex items-center justify-between gap-3 ${sel ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-slate-200 hover:border-slate-400"}`}
                  data-testid={`account-addon-${a.id}`}
                >
                  <span className="flex items-center gap-3">
                    <span className={`h-5 w-5 grid place-items-center border-2 ${sel ? "border-[#00E5FF] bg-[#00E5FF]" : "border-slate-300"}`}>
                      {sel && <span className="text-[10px] text-[#0B132B] font-bold">✓</span>}
                    </span>
                    <span className="font-semibold text-[#0B132B]">{a.name}</span>
                  </span>
                  <span className={`mono ticker-num font-bold ${isDiscount ? "text-[#008B99]" : "text-[#0B132B]"}`}>
                    {isDiscount ? "−" : ""}${Math.abs(a.monthly_price || 0).toFixed(2)}/mo
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {categoryGroups.map((g) => {
        const available = lineLevelAddOnsFor(g.cat.id);
        if (available.length === 0) return null;
        return (
          <div key={g.cat.id} data-testid={`line-addons-${g.cat.id}`}>
            <div className="tw-overline mb-3">{g.cat.name} — per line</div>
            <div className="space-y-3">
              {Array.from({ length: g.lines }, (_, idx) => {
                const selSet = (lineAddOns[g.cat.id] || {})[idx];
                const sel = selSet && typeof selSet.has === "function" ? selSet : new Set();
                return (
                  <div key={idx} className="border border-slate-200 p-3" data-testid={`line-addons-row-${g.cat.id}-${idx + 1}`}>
                    <div className="tw-overline mb-2">Line {idx + 1}</div>
                    <div className="flex flex-wrap gap-2">
                      {available.map((a) => {
                        const isSel = sel.has(a.id);
                        const isDiscount = (a.monthly_price || 0) < 0;
                        return (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => onToggleLine(g.cat.id, idx, a.id)}
                            className={`px-3 py-2 border-2 text-xs font-semibold transition ${isSel ? "border-[#00E5FF] bg-[#00E5FF]/15 text-[#0B132B]" : "border-slate-200 hover:border-slate-400 text-slate-700"}`}
                            data-testid={`line-addon-${g.cat.id}-${idx + 1}-${a.id}`}
                          >
                            {a.name}
                            <span className={`ml-1.5 mono ${isDiscount ? "text-[#008B99]" : "text-slate-500"}`}>
                              {isDiscount ? "−" : ""}${Math.abs(a.monthly_price || 0).toFixed(2)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {addOns.length === 0 && (
        <p className="text-sm text-slate-500 italic">No add-ons configured. Admin can add them under Admin → Add-ons.</p>
      )}
    </div>
  );
}

function round2(n) { return Math.round(n * 100) / 100; }
